import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, IndianRupee, Megaphone, Smartphone } from "lucide-react";

const SECTIONS = [
  {
    id: "finance",
    eyebrow: "01 · Finance",
    title: "Access to formal finance, finally within reach.",
    body: `Access to finance remains a critical barrier for small, rural, and women-led entrepreneurs across India. Women lead nearly 12 million MSMEs in India yet face a financial gap of $333 billion (IFC 2025).

We at Saksham, aim to bridge this gap by helping micropreneurs navigate loan applications for government schemes like MUDRA, Stand-Up India, PMEGP, and beyond.`,
    image: "https://images.pexels.com/photos/8576784/pexels-photo-8576784.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    icon: IndianRupee,
    chips: ["MUDRA", "Stand-Up India", "PMEGP", "Documentation Support"],
    accent: "#09B792",
  },
  {
    id: "marketing",
    eyebrow: "02 · Marketing",
    title: "Visibility that builds brands — not bills.",
    body: `In today's competitive marketplace, delivering good quality products or services isn't enough. Visibility and strategic positioning are equally critical to business success.

For micropreneurs operating with limited budgets and knowledge, this often becomes challenging. Our strategic marketing support focuses on maximising impact through low-cost channels that build brand visibility without draining finance.`,
    image: "https://images.pexels.com/photos/33765890/pexels-photo-33765890.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    icon: Megaphone,
    chips: ["Instagram for Business", "Google Business Profile", "Local SEO", "Brand Story"],
    accent: "#FBD223",
  },
  {
    id: "digital",
    eyebrow: "03 · Digital Tools",
    title: "Practical tech for everyday operations.",
    body: `In an increasingly digital economy, the ability to use basic digital tools is a necessity for small entrepreneurs seeking to compete and grow their businesses.

Our digital literacy support focuses on practical, everyday tools that directly impact business operations — from order management to bookkeeping.`,
    image: "https://images.pexels.com/photos/8837744/pexels-photo-8837744.jpeg?auto=compress&cs=tinysrgb&w=1600&q=80",
    icon: Smartphone,
    chips: ["WhatsApp Business", "UPI & Payments", "Simple Bookkeeping", "Inventory Apps"],
    accent: "#09B792",
  },
];

function SectionHeader() {
  return (
    <section className="bg-[#0A0A0A] text-white pt-40 pb-24">
      <div className="container-saksham">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#FBD223] font-semibold mb-6"
        >
          What We Do
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-6xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight font-medium max-w-5xl"
        >
          Three quiet revolutions, <br className="hidden md:block" />
          <em className="text-[#FBD223] not-italic">one micropreneur at a time.</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          Saksham works at the intersection of <span className="text-white">finance, marketing,</span> and{" "}
          <span className="text-white">digital tools</span> — closing the gaps that hold back India&apos;s small business owners.
        </motion.p>
      </div>
    </section>
  );
}

function Pillar({ s, index }) {
  const reversed = index % 2 === 1;
  const Icon = s.icon;
  return (
    <section id={s.id} data-testid={`pillar-${s.id}`} className={`section-y ${index === 0 ? "bg-[#FAFAFA]" : index === 1 ? "bg-[#F3F1ED]" : "bg-[#FAFAFA]"}`}>
      <div className="container-saksham">
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${reversed ? "md:[&>*:first-child]:order-2" : ""}`}>
          <motion.div
            initial={{ opacity: 0, x: reversed ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-grain opacity-50" />
              <div
                className="absolute bottom-6 left-6 px-5 py-2.5 text-sm md:text-base uppercase tracking-[0.18em] font-semibold"
                style={{ background: s.accent, color: s.accent === "#FBD223" ? "#0A0A0A" : "#FFFFFF" }}
              >
                {s.eyebrow}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-6"
          >
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-10 h-10 flex items-center justify-center" style={{ background: s.accent, color: s.accent === "#FBD223" ? "#0A0A0A" : "#FFFFFF" }}>
                <Icon size={18} />
              </span>
              <span className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#09B792] font-semibold">{s.eyebrow}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight font-medium mb-6">
              {s.title}
            </h2>
            {s.body.split("\n\n").map((para, i) => (
              <p key={i} className="text-[#0A0A0A]/85 text-base md:text-lg leading-relaxed mb-5 font-light">
                {para}
              </p>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function BottomCTA() {
  return (
    <section className="bg-[#09B792] section-y">
      <div className="container-saksham flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium text-white max-w-3xl">
          Know a micropreneur who could <span className="italic">use a hand?</span>
        </h2>
        <Link
          to="/contact"
          data-testid="what-we-do-cta"
          className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#FBD223] hover:bg-[#FBD223] hover:text-[#0A0A0A] px-8 py-4 text-sm font-medium transition-colors whitespace-nowrap"
        >
          Connect with us <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export default function WhatWeDo() {
  return (
    <div data-testid="page-what-we-do">
      <SectionHeader />
      {SECTIONS.map((s, i) => <Pillar key={s.id} s={s} index={i} />)}
      <BottomCTA />
    </div>
  );
}
